/* ---------- Cart gift rules ----------
   Reads the rules rendered as JSON by sections/cart-gift-rules.liquid --
   either a trigger collection, one specific trigger product/variant, or a
   list of trigger products paired with a customer choice of two gift
   options -- mapped to companion/gift product(s), and keeps the cart in
   sync: adds the companion as soon as a qualifying item is in the cart,
   removes it again once none are left, and keeps its quantity matched to
   the total quantity of qualifying trigger items. Runs on every cart change
   anywhere on the site (product page, quick add, cart drawer, cart page) by
   listening to the same PUB_SUB_EVENTS.cartUpdate event Dawn's own cart
   scripts use, plus once on initial load to catch any pre-existing
   mismatch.

   Each rule matches cart items one of two ways (see computeDesiredQuantities):
   - triggerVariantId set -> only that exact variant counts (product rules
     that were narrowed to one variant).
   - triggerVariantId null -> any of triggerProductIds counts, by product,
     regardless of which variant (collection rules, product rules left
     unnarrowed, and choice rules).

   Choice rules (rule.giftOptions is an array of two {productId, variantId,
   title, image}) work like the fixed rules above, except the desired
   quantity goes entirely to whichever option the customer picked rather
   than a single giftVariantId -- see getChosenVariantId. The pick itself is
   stored as a cart attribute, keyed "_gift_choice_<ruleId>" so it survives
   page reloads and section re-renders; it defaults to option 1 the first
   time a trigger item shows up. assets/cart-gift-rules.js also injects a
   small <select> onto that rule's current gift line (see
   renderGiftChoiceControls) so the customer can switch to the other option
   -- selecting it calls selectGiftOption, which swaps the two options'
   quantities and updates the cart attribute in one request.

   This only adds/removes the gift/companion line -- it doesn't make it
   free. A matching Shopify Admin automatic discount (100% off each gift
   product) is still required if a rule is meant to be genuinely free at
   checkout, not just a paid auto-added companion. */

(function () {
  var GIFT_CHOICE_ATTR_PREFIX = '_gift_choice_';

  function getRules() {
    var el = document.getElementById('cart-gift-rules-data');
    if (!el) return [];
    try {
      var rules = JSON.parse(el.textContent);
      return Array.isArray(rules) ? rules.filter(Boolean) : [];
    } catch (e) {
      console.error('[cart-gift-rules] could not parse rules JSON', e);
      return [];
    }
  }

  function isChoiceRule(rule) {
    return Array.isArray(rule.giftOptions);
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  // Mirrors the section id/section-name/selector triples Dawn's own
  // cart.js and cart-drawer.js request after a cart mutation, minus
  // whichever of them aren't present on the current page (cart_type may be
  // "notification" instead of "drawer", and main-cart-items/main-cart-footer
  // only exist on the cart page itself).
  function getGiftSectionsToRender() {
    var sections = [];

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }

    if (document.getElementById('CartDrawer')) {
      sections.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }

    var mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) {
      sections.push({ id: 'main-cart-items', section: mainCartItems.dataset.id, selector: '.js-contents' });
    }

    var mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter) {
      sections.push({ id: 'main-cart-footer', section: mainCartFooter.dataset.id, selector: '.js-contents' });
    }

    if (document.getElementById('cart-live-region-text')) {
      sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    }

    return sections;
  }

  // Every gift/companion product across every rule, fixed or choice -- used
  // to make sure a gift line is never itself mistaken for a trigger, even
  // if its product also happens to sit in a trigger collection/list.
  function getAllGiftProductIds(rules) {
    var ids = [];
    rules.forEach(function (rule) {
      if (isChoiceRule(rule)) {
        rule.giftOptions.forEach(function (option) {
          ids.push(option.productId);
        });
      } else {
        ids.push(rule.giftProductId);
      }
    });
    return ids;
  }

  function countTriggerQuantity(rule, cartItems, giftProductIds) {
    var total = 0;
    cartItems.forEach(function (item) {
      if (giftProductIds.indexOf(item.product_id) !== -1) return;

      var matches = rule.triggerVariantId
        ? item.variant_id === rule.triggerVariantId
        : rule.triggerProductIds.indexOf(item.product_id) !== -1;

      if (matches) total += item.quantity;
    });
    return total;
  }

  // Which of a choice rule's two options the customer currently has picked
  // -- from the cart attribute if it's set and still matches one of the
  // rule's options, otherwise option 1 (the default).
  function getChosenVariantId(rule, cartAttributes) {
    var stored = cartAttributes && cartAttributes[GIFT_CHOICE_ATTR_PREFIX + rule.ruleId];
    var storedVariantId = stored ? parseInt(stored, 10) : null;
    var match = rule.giftOptions.filter(function (option) {
      return option.variantId === storedVariantId;
    })[0];
    return match ? match.variantId : rule.giftOptions[0].variantId;
  }

  function computeDesiredQuantities(rules, cartItems, cartAttributes) {
    var giftProductIds = getAllGiftProductIds(rules);
    var desired = {}; // giftVariantId -> desired quantity

    rules.forEach(function (rule) {
      var total = countTriggerQuantity(rule, cartItems, giftProductIds);

      if (isChoiceRule(rule)) {
        var chosenVariantId = getChosenVariantId(rule, cartAttributes);
        rule.giftOptions.forEach(function (option) {
          var quantity = option.variantId === chosenVariantId ? total : 0;
          // If two rules ever target the same gift variant, combine what
          // each thinks is owed rather than letting one clobber the other.
          desired[option.variantId] = (desired[option.variantId] || 0) + quantity;
        });
      } else {
        desired[rule.giftVariantId] = (desired[rule.giftVariantId] || 0) + total;
      }
    });

    return desired;
  }

  function currentGiftQuantities(rules, cartItems) {
    var current = {};
    rules.forEach(function (rule) {
      if (isChoiceRule(rule)) {
        rule.giftOptions.forEach(function (option) {
          current[option.variantId] = 0;
        });
      } else {
        current[rule.giftVariantId] = 0;
      }
    });
    cartItems.forEach(function (item) {
      if (Object.prototype.hasOwnProperty.call(current, item.variant_id)) {
        current[item.variant_id] += item.quantity;
      }
    });
    return current;
  }

  // While a gift add/remove request is in flight, the checkout button is
  // disabled and shown loading -- same .loading/.loading__spinner pattern
  // Dawn's own product-form.js uses -- so a customer can't reach checkout
  // mid-reconcile with a gift line that's about to be added or removed out
  // from under them. The button gets replaced wholesale by the section
  // re-render this wraps (see applyGiftUpdates below), so the "off" state
  // set here is really just a safety net for the error/no-op paths.
  function setCheckoutButtonLoading(isLoading) {
    var button = document.getElementById('CartDrawer-Checkout');
    if (!button) return;
    var spinner = button.querySelector('.loading__spinner');

    button.disabled = isLoading;
    button.classList.toggle('loading', isLoading);
    if (isLoading) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  // `attributes` is optional -- only choice-rule switches need to persist a
  // cart attribute alongside the quantity swap, in the same request.
  function applyGiftUpdates(updates, attributes) {
    var sectionsToRender = getGiftSectionsToRender();

    var body = {
      updates: updates,
      sections: sectionsToRender.map(function (section) {
        return section.section;
      }),
      sections_url: window.location.pathname,
    };
    if (attributes) body.attributes = attributes;

    setCheckoutButtonLoading(true);

    return fetch(routes.cart_update_url, Object.assign({}, fetchConfig(), { body: JSON.stringify(body) }))
      .then(function (response) {
        return response.json();
      })
      .then(function (parsedState) {
        if (parsedState.errors) {
          console.error('[cart-gift-rules]', parsedState.errors);
          return parsedState;
        }

        sectionsToRender.forEach(function (section) {
          if (!parsedState.sections || !parsedState.sections[section.section]) return;
          var container = document.getElementById(section.id);
          if (!container) return;
          var target = container.querySelector(section.selector) || container;
          target.innerHTML = getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });

        var cartDrawerWrapper = document.querySelector('cart-drawer');
        var cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-gift-rules', cartData: parsedState });

        return parsedState;
      })
      .catch(function (e) {
        console.error('[cart-gift-rules]', e);
      })
      .finally(function () {
        setCheckoutButtonLoading(false);
      });
  }

  // Injects a small "Free gift: [select]" control onto a choice rule's
  // current gift line, letting the customer switch to the other option.
  // Runs after every render (initial load + after every reconcile/apply),
  // since the section re-renders this wraps replace the gift line's markup
  // wholesale each time -- there's nothing to preserve, just re-inject.
  // Cart lines are tagged with data-gift-product-id in
  // snippets/cart-drawer.liquid and sections/main-cart-items.liquid.
  //
  // Two things guard against decorating the WRONG line: a merchant can
  // reuse the same physical gift product across more than one rule (e.g.
  // the same "sticks set" as a fixed Gift rule (collection)'s gift AND as
  // one of a choice rule's two options) -- matching on product id alone
  // would then inject a choice rule's selector onto a gift line that a
  // completely unrelated rule actually added.
  //   1. Only consider a choice rule at all if it's currently ACTIVE (one
  //      of its own trigger products is actually in the cart) -- otherwise
  //      any gift line sharing one of its option product ids belongs to
  //      some other rule, not this one.
  //   2. Only match the line for the CURRENTLY CHOSEN option's product id,
  //      not "either of the two options" -- the unchosen option shouldn't
  //      have a cart line at all under normal operation.
  function renderGiftChoiceControls(rules, cartItems, cartAttributes) {
    var choiceRules = rules.filter(isChoiceRule);
    if (!choiceRules.length) return;

    var giftProductIds = getAllGiftProductIds(rules);

    choiceRules.forEach(function (rule) {
      var isActive = countTriggerQuantity(rule, cartItems, giftProductIds) > 0;
      if (!isActive) return;

      var chosenVariantId = getChosenVariantId(rule, cartAttributes);
      var chosenOption = rule.giftOptions.filter(function (option) {
        return option.variantId === chosenVariantId;
      })[0];
      if (!chosenOption) return;

      document.querySelectorAll('[data-gift-product-id="' + chosenOption.productId + '"]').forEach(function (line) {
        if (line.querySelector('[data-gift-choice-select]')) return; // already has one

        var select = document.createElement('select');
        select.className = 'cart-gift-choice__select';
        select.setAttribute('data-gift-choice-select', 'true');
        select.setAttribute('aria-label', 'Choose your free gift');
        select.innerHTML = rule.giftOptions
          .map(function (option) {
            var selected = option.variantId === chosenVariantId ? ' selected' : '';
            return '<option value="' + option.variantId + '"' + selected + '>' + escapeHtml(option.title) + '</option>';
          })
          .join('');

        select.addEventListener('change', function () {
          selectGiftOption(rule.ruleId, parseInt(select.value, 10));
        });

        var wrapper = document.createElement('div');
        wrapper.className = 'cart-gift-choice';
        var label = document.createElement('span');
        label.className = 'cart-gift-choice__label';
        label.textContent = 'Select sticks set size:';
        wrapper.appendChild(label);
        wrapper.appendChild(select);
        line.appendChild(wrapper);
      });
    });
  }

  // Called when the customer picks the other option on a choice rule's
  // selector. Re-fetches the cart (rather than trusting whatever quantity
  // is already showing) so the swap is correct even if something else
  // changed the cart a moment earlier, persists the pick as a cart
  // attribute, and recomputes desired quantities across EVERY rule (not
  // just this one) using computeDesiredQuantities -- the same function
  // reconcileCartGifts uses -- rather than a narrower update scoped to only
  // this rule's two options. That narrower approach would incorrectly
  // clobber another rule's contribution to the same variant if a merchant
  // ever reuses one physical gift product across more than one rule (see
  // the comment above renderGiftChoiceControls for the same scenario).
  function selectGiftOption(ruleId, newVariantId) {
    var rules = getRules();
    var rule = rules.filter(function (r) {
      return r.ruleId === ruleId;
    })[0];
    if (!rule || !isChoiceRule(rule)) return;

    var attributes = {};
    attributes[GIFT_CHOICE_ATTR_PREFIX + ruleId] = String(newVariantId);

    fetch(routes.cart_url + '.json')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (!cart || !cart.items) return;

        var mergedAttributes = Object.assign({}, cart.attributes, attributes);
        var desired = computeDesiredQuantities(rules, cart.items, mergedAttributes);

        var updates = {};
        Object.keys(desired).forEach(function (variantId) {
          updates[variantId] = desired[variantId];
        });

        return applyGiftUpdates(updates, attributes).then(function () {
          renderGiftChoiceControls(rules, cart.items, mergedAttributes);
        });
      })
      .catch(function (e) {
        console.error('[cart-gift-rules]', e);
      });
  }

  var reconciling = false;
  var reconcileAgain = false;

  function reconcileCartGifts() {
    var rules = getRules();
    if (!rules.length) return Promise.resolve();

    if (reconciling) {
      // A cart mutation landed while we were mid-reconcile (e.g. the
      // sections we just patched triggered another cartUpdate) -- rerun
      // once the in-flight pass finishes instead of overlapping requests.
      reconcileAgain = true;
      return Promise.resolve();
    }
    reconciling = true;

    return fetch(routes.cart_url + '.json')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (!cart || !cart.items) return;

        var desired = computeDesiredQuantities(rules, cart.items, cart.attributes);
        var current = currentGiftQuantities(rules, cart.items);

        var updates = {};
        var changed = false;
        Object.keys(desired).forEach(function (variantId) {
          if (desired[variantId] !== (current[variantId] || 0)) {
            changed = true;
          }
          updates[variantId] = desired[variantId];
        });

        var afterUpdate = changed ? applyGiftUpdates(updates) : Promise.resolve();
        return afterUpdate.then(function () {
          renderGiftChoiceControls(rules, cart.items, cart.attributes);
        });
      })
      .catch(function (e) {
        console.error('[cart-gift-rules]', e);
      })
      .finally(function () {
        reconciling = false;
        if (reconcileAgain) {
          reconcileAgain = false;
          reconcileCartGifts();
        }
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    reconcileCartGifts();

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event.source === 'cart-gift-rules') return;
      reconcileCartGifts();
    });
  });
})();
